import React from 'react';
import { Autocomplete, AutocompleteProps, Box } from '@mui/material';
import TextInput from './textInput';

export type AutocompleteOption<T = string> = {
  label: string;
  value: T;
  icon?: React.ReactNode; // Optional icon/flag to display
};

type AutocompleteInputProps<T = string> = Omit<
  AutocompleteProps<AutocompleteOption<T>, false, false, false>,
  'options' | 'renderInput' | 'getOptionLabel' | 'value' | 'onChange'
> & {
  label: string;
  options: AutocompleteOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
};

export const AutocompleteInput = <T extends string | number = string>({
  label,
  options,
  value,
  onChange,
  size = 'small',
  fullWidth = true,
  error,
  helperText,
  required,
  ...autocompleteProps
}: AutocompleteInputProps<T>) => {
  const selected = options.find(o => o.value === value) ?? null;

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      options={options}
      getOptionLabel={(option) => option.label}
      value={selected}
      onChange={(_, newValue) => onChange(newValue?.value ?? ('' as T))}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
          {option.icon && <span>{option.icon}</span>}
          <span>{option.label}</span>
        </Box>
      )}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: true },
          ],
        },
        paper: {
          sx: { borderRadius: 2, mt: 1 },
        },
      }}
      renderInput={(params) => (
        <TextInput
          {...params}
          label={label}
          size={size}
          fullWidth={fullWidth}
          variant="outlined"
          error={error}
          helperText={helperText}
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected?.icon ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>{selected.icon}</span>
              </Box>
            ) : params.InputProps.startAdornment,
          }}
        />
      )}
      {...autocompleteProps}
    />
  );
};

